/**
 * 智能预加载 — 视口预测
 *
 * 功能：
 * 1. 监听画布滚动/缩放，计算当前视口范围
 * 2. 根据滚动方向和速度，预测用户接下来会看到的区域
 * 3. 提前加载即将进入视口的节点
 *
 * 使用示例：
 *   predictivePreloader.init({
 *     reactFlowInstance,
 *     nodes,
 *     priorityQueue,
 *     fetchCallback: (node) => localCacheService.getLocalUrlOrFetch(...)
 *   });
 */

class PredictivePreloader {
  constructor() {
    this.reactFlowInstance = null;
    this.nodes = [];
    this.priorityQueue = null;
    this.fetchCallback = null;

    // 滚动速度追踪
    this.lastViewport = null;
    this.lastUpdateTime = null;
    this.scrollVelocity = { x: 0, y: 0 }; // 像素/秒

    // 防抖定时器
    this.preloadTimer = null;
    this.updateTimer = null;

    // 已预加载的节点（避免重复预加载）
    this.preloadedNodes = new Set();

    // 配置
    this.config = {
      debounceMs: 300,           // 防抖延迟（ms）
      lookAheadSeconds: 2,       // 预测未来 N 秒的位置
      maxPreloadCount: 8,        // 单次最多预加载 N 个节点
      viewportPadding: 1.5,      // 视口扩展倍数（1.5 = 当前视口 + 周围 50%）
    };
  }

  /**
   * 初始化预加载器
   * @param {Object} options
   * @param {Object} options.reactFlowInstance - React Flow 实例
   * @param {Array} options.nodes - 节点列表
   * @param {Object} options.priorityQueue - 优先级队列实例
   * @param {Function} options.fetchCallback - 下载回调 (node) => Promise
   */
  init({ reactFlowInstance, nodes, priorityQueue, fetchCallback }) {
    this.reactFlowInstance = reactFlowInstance;
    this.nodes = nodes;
    this.priorityQueue = priorityQueue;
    this.fetchCallback = fetchCallback;

    // 初始化视口追踪
    this._updateViewport();
  }

  /**
   * 更新节点列表（当 EditorPage.nodes 变化时调用）
   * @param {Array} nodes
   */
  updateNodes(nodes) {
    this.nodes = nodes;
    this._schedulePreload();
  }

  /**
   * 视口变化时调用（滚动/缩放）
   */
  onViewportChange() {
    this._updateViewport();
    this._schedulePreload();
  }

  /**
   * 更新视口状态并计算滚动速度
   * @private
   */
  _updateViewport() {
    if (!this.reactFlowInstance) return;

    const viewport = this.reactFlowInstance.getViewport();
    const now = Date.now();

    if (this.lastViewport && this.lastUpdateTime) {
      const deltaTime = (now - this.lastUpdateTime) / 1000; // 秒
      const deltaX = (viewport.x - this.lastViewport.x) / viewport.zoom;
      const deltaY = (viewport.y - this.lastViewport.y) / viewport.zoom;

      // 计算速度（像素/秒）
      if (deltaTime > 0) {
        this.scrollVelocity = {
          x: deltaX / deltaTime,
          y: deltaY / deltaTime,
        };
      }
    }

    this.lastViewport = viewport;
    this.lastUpdateTime = now;
  }

  /**
   * 调度预加载（防抖）
   * @private
   */
  _schedulePreload() {
    clearTimeout(this.preloadTimer);
    this.preloadTimer = setTimeout(() => {
      this._executePreload();
    }, this.config.debounceMs);
  }

  /**
   * 执行预加载
   * @private
   */
  _executePreload() {
    if (!this.reactFlowInstance || !this.priorityQueue || !this.fetchCallback) {
      return;
    }

    const viewport = this.reactFlowInstance.getViewport();
    if (!viewport) return;

    // 计算当前视口范围（画布坐标）
    const currentBounds = this._getViewportBounds(viewport);

    // 预测未来视口范围
    const futureBounds = this._predictFutureViewport(currentBounds);

    // 筛选即将进入视口的节点
    const upcomingNodes = this._getNodesInBounds(futureBounds)
      .filter(node => !node.data.localCacheUrl)  // 未缓存
      .filter(node => !this.preloadedNodes.has(node.id))  // 未预加载过
      .slice(0, this.config.maxPreloadCount);  // 限制数量

    if (upcomingNodes.length === 0) return;

    console.log(`📦 预加载 ${upcomingNodes.length} 个即将进入视口的节点`);

    // 添加到低优先级队列
    upcomingNodes.forEach(node => {
      this.preloadedNodes.add(node.id);

      this.priorityQueue.add(
        () => this.fetchCallback(node),
        'low',
        `preload-${node.id}`
      ).catch(err => {
        console.debug(`预加载失败，跳过: ${node.id}`, err.message);
      });
    });
  }

  /**
   * 获取当前视口范围（画布坐标）
   * @private
   * @param {Object} viewport - {x, y, zoom}
   * @returns {Object} - {left, right, top, bottom}
   */
  _getViewportBounds(viewport) {
    const container = this.reactFlowInstance.flowToScreenPosition({ x: 0, y: 0 }).container;
    if (!container) {
      // 降级：使用 window 尺寸
      const width = window.innerWidth;
      const height = window.innerHeight;

      return {
        left: -viewport.x / viewport.zoom,
        right: (width - viewport.x) / viewport.zoom,
        top: -viewport.y / viewport.zoom,
        bottom: (height - viewport.y) / viewport.zoom,
      };
    }

    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;

    return {
      left: -viewport.x / viewport.zoom,
      right: (width - viewport.x) / viewport.zoom,
      top: -viewport.y / viewport.zoom,
      bottom: (height - viewport.y) / viewport.zoom,
    };
  }

  /**
   * 预测未来视口范围
   * @private
   * @param {Object} currentBounds
   * @returns {Object}
   */
  _predictFutureViewport(currentBounds) {
    const { x: vx, y: vy } = this.scrollVelocity;
    const { lookAheadSeconds, viewportPadding } = this.config;

    // 根据滚动速度预测位移
    const predictedDeltaX = vx * lookAheadSeconds;
    const predictedDeltaY = vy * lookAheadSeconds;

    // 扩展视口范围
    const width = currentBounds.right - currentBounds.left;
    const height = currentBounds.bottom - currentBounds.top;
    const paddingX = width * (viewportPadding - 1) / 2;
    const paddingY = height * (viewportPadding - 1) / 2;

    return {
      left: currentBounds.left + predictedDeltaX - paddingX,
      right: currentBounds.right + predictedDeltaX + paddingX,
      top: currentBounds.top + predictedDeltaY - paddingY,
      bottom: currentBounds.bottom + predictedDeltaY + paddingY,
    };
  }

  /**
   * 获取指定范围内的节点
   * @private
   * @param {Object} bounds - {left, right, top, bottom}
   * @returns {Array}
   */
  _getNodesInBounds(bounds) {
    return this.nodes.filter(node => {
      if (!node.position) return false;
      if (node.type !== 'aiNode') return false;
      if (!node.data.output) return false;

      const nodeWidth = node.data?.customSize?.width || 240;
      const nodeHeight = node.data?.customSize?.height || 240;

      const nodeLeft = node.position.x;
      const nodeRight = node.position.x + nodeWidth;
      const nodeTop = node.position.y;
      const nodeBottom = node.position.y + nodeHeight;

      // 判断节点是否与视口相交
      return !(
        nodeRight < bounds.left ||
        nodeLeft > bounds.right ||
        nodeBottom < bounds.top ||
        nodeTop > bounds.bottom
      );
    });
  }

  /**
   * 清理资源（组件卸载时调用）
   */
  destroy() {
    clearTimeout(this.preloadTimer);
    clearTimeout(this.updateTimer);
    this.preloadedNodes.clear();
    this.reactFlowInstance = null;
    this.nodes = [];
    this.priorityQueue = null;
    this.fetchCallback = null;
  }
}

// 导出单例
const predictivePreloader = new PredictivePreloader();
export default predictivePreloader;
